import React from 'react';
import { Box, Checkbox, ToggleButton, Tooltip, Typography } from '@mui/material';
import { lighten, SxProps, Theme } from '@mui/material/styles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsIcon from '@mui/icons-material/Sms';
import LockIcon from '@mui/icons-material/Lock';
import { OWNER_LOCK_TOOLTIP } from './notificationStrings';

/**
 * The accent ("on") colour for notification cell controls lives in ONE place so
 * it can be swapped for the SecurSpace brand orange in a single edit:
 *   const accent = theme.palette.brandOrange.main;
 * It currently reads the theme indigo (palette.primary.main) to match the Save
 * button and the design's shipped selection.
 */
const accentColor = (theme: Theme) => theme.palette.primary.main;
const lockedFill = (theme: Theme) => lighten(accentColor(theme), 0.22); // ≈ 78% accent + white

const OFF_BORDER = '#E0E0E0';
const OFF_ICON = '#94A3B8';
const LOCK_BADGE_BG = '#334155';

export type ChannelControlStyle = 'chip' | 'checkbox';

interface ChannelControlProps {
  channelValue: string;
  channelLabel: string;
  on: boolean;
  locked: boolean;
  controlStyle: ChannelControlStyle;
  /** Show the channel label beside the control (cards layout). */
  showLabel?: boolean;
  onToggle: () => void;
}

export const getChannelIcon = (channelValue: string, sx?: SxProps<Theme>) => {
  switch (channelValue) {
    case 'SMS':
      return <SmsIcon sx={sx} aria-hidden />;
    case 'EMAIL':
    default:
      return <MailOutlineIcon sx={sx} aria-hidden />;
  }
};

const ChannelControl = ({
  channelValue,
  channelLabel,
  on,
  locked,
  controlStyle,
  showLabel = false,
  onToggle,
}: ChannelControlProps) => {
  const ariaLabel = `${channelLabel}${on ? ' on' : ' off'}${locked ? ' (required)' : ''}`;

  const lockBadge = locked ? (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: -6,
        right: -6,
        width: 16,
        height: 16,
        borderRadius: '999px',
        backgroundColor: LOCK_BADGE_BG,
        color: '#fff',
        border: '2px solid #fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LockIcon sx={{ fontSize: 8 }} />
    </Box>
  ) : null;

  let control: React.ReactNode;

  if (controlStyle === 'checkbox') {
    control = (
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: showLabel ? 1 : 0 }}>
        <Checkbox
          checked={on}
          disabled={locked}
          onChange={onToggle}
          inputProps={{ 'aria-label': ariaLabel }}
          sx={{
            p: 0.5,
            color: OFF_ICON,
            '&.Mui-checked': { color: accentColor },
            '&.Mui-checked.Mui-disabled': { color: lockedFill },
          }}
        />
        {showLabel && <Typography sx={{ fontSize: 14 }}>{channelLabel}</Typography>}
        {lockBadge}
      </Box>
    );
  } else {
    control = (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <ToggleButton
          value={channelValue}
          selected={on}
          disabled={locked}
          onChange={onToggle}
          aria-label={ariaLabel}
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            border: `1px solid ${OFF_BORDER}`,
            backgroundColor: '#fff',
            color: OFF_ICON,
            width: showLabel ? 'auto' : 40,
            height: 40,
            minWidth: 40,
            px: showLabel ? 2 : 0,
            gap: showLabel ? 0.75 : 0,
            transition: 'all 0.18s cubic-bezier(0.4,0,0.2,1)',
            '&:hover': {
              borderColor: accentColor,
              color: accentColor,
              backgroundColor: (theme) => lighten(accentColor(theme), 0.92),
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: accentColor,
              borderColor: accentColor,
              color: '#fff',
            },
            '&.Mui-disabled': {
              backgroundColor: lockedFill,
              borderColor: 'transparent',
              color: '#fff',
            },
            '&:active:not(.Mui-disabled)': { transform: 'translateY(1px)' },
          }}
        >
          {getChannelIcon(channelValue, { fontSize: 18 })}
          {showLabel && (
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
              {channelLabel}
            </Typography>
          )}
        </ToggleButton>
        {lockBadge}
      </Box>
    );
  }

  if (!locked) return <>{control}</>;

  // Wrap in a span so the Tooltip works over the disabled (locked) control.
  return (
    <Tooltip title={OWNER_LOCK_TOOLTIP} arrow placement="top">
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {control}
      </Box>
    </Tooltip>
  );
};

export { ChannelControl };
